import React, { useState, useEffect } from "react";
import axios from "axios";
import { jwtDecode } from "jwt-decode";
import { useNavigate } from "react-router-dom";
import "./baseStyles.css";
import "./MyFavoritesPage.css";

const apiUrl = process.env.REACT_APP_API_URL;

const QAPage = () => {
  const navigate = useNavigate();

  // Core state
  const [loginUserId, setLoginUserId] = useState("");
  const [campaignName, setCampaignName] = useState("");
  const [question, setQuestion] = useState(null);
  const [answers, setAnswers] = useState({
    Answer1: "",
    Answer2: "",
    Guess1: "",
    Guess2: "",
  });
  const [matchResults, setMatchResults] = useState([]);
  const [matchPartnerResults, setMatchPartnerResults] = useState([]);
  const [partnerResponses, setPartnerResponses] = useState({
    partnerAnswer1: "",
    partnerAnswer2: "",
    partnerGuess1: "",
    partnerGuess2: "",
    comments: [],
  });
  const [partnerFirstName, setPartnerFirstName] = useState("Partner");
  const [error, setError] = useState("");
  const [infoMessage, setInfoMessage] = useState("");
  const [showResults, setShowResults] = useState(false);

  // Tally state
  const [showTally, setShowTally] = useState(false);
  const [tallyLoading, setTallyLoading] = useState(false);
  const [tallyError, setTallyError] = useState("");
  const [tallyOverall, setTallyOverall] = useState(null);

  // Helper: handle Lambda proxy vs direct
  const parseLambdaResponse = (res) => {
    if (!res) return null;
    const d = res.data ?? res;
    if (d && typeof d.body === "string") {
      try {
        return JSON.parse(d.body);
      } catch {
        return d.body;
      }
    }
    if (d && typeof d.body === "object") return d.body;
    return d;
  };

  const clearStatusMessages = () => {
    setError("");
    setInfoMessage("");
  };

  // ----------------- Auth + initial load -----------------
  useEffect(() => {
    const idToken = localStorage.getItem("idToken");

    if (!idToken) {
      setError("User is not authenticated. Please log in.");
      navigate("/login");
      return;
    }

    try {
      const decodedToken = jwtDecode(idToken);

      if (decodedToken.exp * 1000 < Date.now()) {
        localStorage.removeItem("idToken");
        setError("Session expired. Please log in again.");
        navigate("/login");
        return;
      }

      const userId = decodedToken.sub;
      const loginUserIdValue = decodedToken["cognito:username"];
      setLoginUserId(loginUserIdValue);

      const campaign = `${userId}_Favorites`;
      setCampaignName(campaign);

      fetchCurrentQuestion(userId, campaign);
    } catch (e) {
      console.error("Error decoding token:", e);
      localStorage.removeItem("idToken");
      setError("Authentication error. Please log in again.");
      navigate("/login");
    }
  }, [navigate]);

  // Info message: partner status
  useEffect(() => {
    if (!question?.partnerHasSubmitted) {
      setInfoMessage(`${partnerFirstName} has not answered yet.`);
    } else {
      setInfoMessage("");
    }
  }, [question?.partnerHasSubmitted, partnerFirstName]);

  // Show match results only when both have submitted
  useEffect(() => {
    if (question?.userHasSubmitted && question?.partnerHasSubmitted) {
      setShowResults(true);
    } else {
      setShowResults(false);
    }
  }, [question]);

  // ----------------- API calls -----------------
  const fetchCurrentQuestion = async (userId, campaign) => {
    try {
      const idToken = localStorage.getItem("idToken");
      const response = await axios.post(
        apiUrl,
        {
          operation: "read_and_compare_current_item",
          userid: userId,
          campaignName: campaign,
        },
        {
          headers: { Authorization: `Bearer ${idToken}` },
        }
      );

      const data = parseLambdaResponse(response);
      console.log("Fetched Data:", data);

      if (!data) {
        setError("No question data available.");
        window.scrollTo({ top: 0, behavior: "smooth" });
        setPartnerResponses({});
        return;
      }

      setQuestion(data);
      setAnswers({
        Answer1: data.AnswersAndGuesses?.Answer1 || "",
        Answer2: data.AnswersAndGuesses?.Answer2 || "",
        Guess1: data.AnswersAndGuesses?.Guess1 || "",
        Guess2: data.AnswersAndGuesses?.Guess2 || "",
      });
      setMatchResults([data.AnswersAndGuesses]);
      setMatchPartnerResults([data.PartnerResponses]);
      setPartnerResponses(data.PartnerResponses || {});
      setPartnerFirstName(data.partnerFirstName || "Partner");
    } catch (err) {
      console.error("Fetch Error:", err);

      if (err.response) {
        const { status, data } = err.response;
        window.scrollTo({ top: 0, behavior: "smooth" });

        if (status === 404) {
          if (data?.error === "User or partner ID missing.") {
            setError(
              "A required user or partner ID is missing. Please ensure both you and your partner are registered."
            );
          } else if (data?.error === "Campaign list entry not found.") {
            setError("No campaign found. Please start a campaign.");
          } else {
            setError(`Not Found: ${data?.error || "Requested data could not be found."}`);
          }
        } else if (status === 400) {
          setError(`Bad Request: ${data?.error || "Invalid request. Please check your input."}`);
        } else if (status === 500) {
          setError(`Server Error: ${data?.error || "An unexpected error occurred. Please try again later."}`);
        } else {
          setError(`Unexpected Error: ${data?.error || "Something went wrong. Please try again."}`);
        }
      } else if (err.request) {
        setError("No response received from server. Please check your internet connection.");
      } else {
        setError(`Unexpected Error: ${err.message || "An unknown error occurred."}`);
      }
    }
  };

  const handleSubmit = async () => {
    clearStatusMessages();

    if (
      !answers.Answer1.trim() ||
      !answers.Answer2.trim() ||
      !answers.Guess1.trim() ||
      !answers.Guess2.trim()
    ) {
      setError("Please complete all answer fields before submitting.");
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }

    try {
      const idToken = localStorage.getItem("idToken");
      const userId = jwtDecode(idToken).sub;

      const payload = {
        operation: "update_campaign_answers",
        userid: userId,
        loginUserId,
        campaignName,
        campaignItemsId: question.campaignItemsId,
        ...answers,
      };

      await axios.post(
        apiUrl,
        payload,
        { headers: { Authorization: `Bearer ${idToken}` } }
      );

      setInfoMessage("Answers submitted successfully!");
      fetchCurrentQuestion(userId, campaignName);
    } catch (err) {
      console.error("Submission Error:", err);
      setInfoMessage("Failed to submit answers. Please try again.");
      setError("Failed to submit answers.");
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const handleMoveOn = async () => {
    try {
      const idToken = localStorage.getItem("idToken");
      const userId = jwtDecode(idToken).sub;

      await axios.post(
        apiUrl,
        {
          operation: "update_campaign_item",
          userid: userId,
          campaignName,
          campaignItemsId: question.campaignItemsId,
          questionHasBeenReviewed: "Y",
        },
        { headers: { Authorization: `Bearer ${idToken}` } }
      );

      if (question?.partnerHasSubmitted === false) {
        setInfoMessage(`${question.partnerFirstName || "Your partner"} has not answered yet.`);
      } else {
        setInfoMessage("Marked as reviewed. Moving to the next question.");
      }

      fetchCurrentQuestion(userId, campaignName);
    } catch (err) {
      console.error("Review Error:", err);
      setError("Failed to mark question as reviewed.");
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const handleTallyFavorites = async () => {
    setShowTally(true);
    setTallyLoading(true);
    setTallyError("");
    setTallyOverall(null);

    try {
      const idToken = localStorage.getItem("idToken");
      if (!idToken) {
        setTallyError("Not authenticated. Please log in again.");
        setTallyLoading(false);
        return;
      }

      const decoded = jwtDecode(idToken);
      const userId = decoded.sub;

      const res = await axios.post(
        apiUrl,
        {
          operation: "tally_favorites",
          userid: userId,
          campaignName,
        },
        { headers: { Authorization: `Bearer ${idToken}` } }
      );

      const data = parseLambdaResponse(res);
      if (!data || !data.overall) {
        setTallyError("No tally data returned.");
      } else {
        setTallyOverall(data.overall);
      }
    } catch (err) {
      console.error("Tally Error:", err);
      if (err.response?.data?.error) {
        setTallyError(err.response.data.error);
      } else {
        setTallyError("Failed to fetch tally results.");
      }
    } finally {
      setTallyLoading(false);
    }
  };

  const inputsDisabled = question?.userHasSubmitted && question?.partnerHasSubmitted;



  // Derived tally totals for clearer wording
  const yourG1Correct = tallyOverall?.your?.g1_correct ?? 0;
  const yourG1Wrong = tallyOverall?.your?.g1_wrong ?? 0;
  const yourG1Total = yourG1Correct + yourG1Wrong;

  const yourG2Correct = tallyOverall?.your?.g2_correct ?? 0;
  const yourG2Wrong = tallyOverall?.your?.g2_wrong ?? 0;
  const yourG2Total = yourG2Correct + yourG2Wrong;

  const theirG1Correct = tallyOverall?.their?.g1_correct ?? 0;
  const theirG1Wrong = tallyOverall?.their?.g1_wrong ?? 0;
  const theirG1Total = theirG1Correct + theirG1Wrong;

  const theirG2Correct = tallyOverall?.their?.g2_correct ?? 0;
  const theirG2Wrong = tallyOverall?.their?.g2_wrong ?? 0;
  const theirG2Total = theirG2Correct + theirG2Wrong;






  // ----------------- Render -----------------
  return (
    <div className="container">
      <div className="header">
        <h2>myFavorites List</h2>
        <br />
        {error && <div className="error-message">{error}</div>}
        {error && <div style={{ marginBottom: "16px" }} />}
      </div>

      {loginUserId && question && (
        <div className="card">
          <h3>{question.campaignItemsText}</h3>
          <br />

          <div className="form-group-wrapper">
            {/* Your answers */}
            <div className="response-card">
              <div className="response-header">Your Most Favorite:</div>
              <input
                type="text"
                value={answers.Answer1}
                onChange={(e) =>
                  setAnswers((prev) => ({ ...prev, Answer1: e.target.value }))
                }
                disabled={inputsDisabled}
                className={`input-box ${inputsDisabled ? "disabled-input" : ""}`}
              />
              <div className="match-note">
                {showResults
                  ? matchPartnerResults[0]?.Match1
                    ? `${partnerFirstName} got it!`
                    : `${partnerFirstName} missed this one! (Guessed: ${
                        partnerResponses.partnerGuess1 || "No guess yet"
                      })`
                  : ""}
              </div>

              <div className="response-header">Your 2nd most Favorite:</div>
              <input
                type="text"
                value={answers.Answer2}
                onChange={(e) =>
                  setAnswers((prev) => ({ ...prev, Answer2: e.target.value }))
                }
                disabled={inputsDisabled}
                className={`input-box ${inputsDisabled ? "disabled-input" : ""}`}
              />
              <div className="match-note">
                {showResults
                  ? matchPartnerResults[0]?.Match2
                    ? `${partnerFirstName} got it!`
                    : `${partnerFirstName} missed this one! (Guessed: ${
                        partnerResponses.partnerGuess2 || "No guess yet"
                      })`
                  : ""}
              </div>
            </div>

            <br />

            {/* Your guesses */}
            <div className="response-card">
              <div className="response-header">Your guesses:</div>

              <div className="response-header">{partnerFirstName}'s most favorite:</div>
              <input
                type="text"
                value={answers.Guess1}
                onChange={(e) =>
                  setAnswers((prev) => ({ ...prev, Guess1: e.target.value }))
                }
                disabled={inputsDisabled}
                className={`input-box ${inputsDisabled ? "disabled-input" : ""}`}
              />
              <div className="match-note">
                {showResults
                  ? matchResults[0]?.Match1
                    ? `You got it!`
                    : `You missed this one! (Guessed: ${
                        partnerResponses.partnerAnswer1 || "No guess yet"
                      })`
                  : ""}
              </div>

              <div className="response-header">{partnerFirstName}'s 2nd favorite is:</div>
              <input
                type="text"
                value={answers.Guess2}
                onChange={(e) =>
                  setAnswers((prev) => ({ ...prev, Guess2: e.target.value }))
                }
                disabled={inputsDisabled}
                className={`input-box ${inputsDisabled ? "disabled-input" : ""}`}
              />
              <div className="match-note">
                {showResults
                  ? matchResults[0]?.Match2
                    ? `You got it!`
                    : `You missed this one! (Guessed: ${
                        partnerResponses.partnerAnswer2 || "No guess yet"
                      })`
                  : ""}
              </div>
            </div>
          </div>

          <br />

          {/* Submit + info */}
          <div className="submit-container">
            <br />
            <button
              className="save-button"
              onClick={handleSubmit}
              disabled={inputsDisabled}
              style={{
                opacity: inputsDisabled ? 0.6 : 1,
                cursor: inputsDisabled ? "not-allowed" : "pointer",
              }}
            >
              Submit
            </button>

            {question?.userHasSubmitted &&
              question?.partnerHasSubmitted &&
              question?.questionHasBeenReviewed !== "Y" && (
                <button className="save-button" onClick={handleMoveOn}>
                  Done
                </button>
              )}

            <br />
            <br />
            <br />
            {infoMessage && <span className="info-message">{infoMessage}</span>}
          </div>

          <br />

          {/* History + Tally buttons */}
          <div className="button-row">
            <button
              className="save-button"
              onClick={() => navigate("/favorites-history")}
            >
              History
            </button>
            <button className="save-button" onClick={handleTallyFavorites}>
              How are we doing?
            </button>
          </div>




          {/* Tally modal */}
          {showTally && (
            <div className="modal" onClick={() => setShowTally(false)}>
              <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                  <h3>How are we doing?</h3>
                </div>
<br/>
                {tallyLoading && <p>Loading…</p>}

                {!tallyLoading && tallyError && (
                  <p className="error-message" style={{ margin: 0 }}>
                    {tallyError}
                  </p>
                )}

                {!tallyLoading && !tallyError && tallyOverall && (
                  <div className="tally-body tally-compact">
                    <div className="tally-section">
                      <div className="tally-title">
                        You guessing {partnerFirstName}
                      </div>
                      <p>
                        Most favorite:{" "}
                        <strong>
                          {yourG1Correct}/{yourG1Total || 0}
                        </strong>
                      </p>
                      <p>
                        2nd favorite:{" "}
                        <strong>
                          {yourG2Correct}/{yourG2Total || 0}
                        </strong>
                      </p>
<br/>
                    </div>
<br/>

                    <div className="tally-section">
                      <div className="tally-title">
                        {partnerFirstName} guessing you
                      </div>
                      <p>
                        Most favorite:{" "}
                        <strong>
                          {theirG1Correct}/{theirG1Total || 0}
                        </strong>
                      </p>
                      <p>
                        2nd favorite:{" "}
                        <strong>
                          {theirG2Correct}/{theirG2Total || 0}
                        </strong>
                      </p>
                    </div>
                  </div>
                )}

                <div className="tally-footer">
                  <button
                    type="button"
                    className="save-button"
                    onClick={() => setShowTally(false)}
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          )}





        </div>
      )}
    </div>
  );
};

export default QAPage;
